import { useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { Rocket, Sparkles, CheckCircle, TrendingUp, Zap, Users, Globe } from "lucide-react";

// Company names — mix of real-sounding and plausible home service businesses
const companyNames = [
  "Precision Air Solutions",
  "Summit Plumbing & HVAC",
  "Liberty Home Services",
  "Elite Comfort Systems",
  "ProTech Mechanical",
  "AllStar Heating & Cooling",
  "Patriot Plumbing Co.",
  "NextGen Air Systems",
  "Reliable Home Pros",
  "ClearFlow Plumbing",
  "AirMax Comfort",
  "GreenLeaf HVAC",
  "TrueBlue Mechanical",
  "Peak Performance Air",
  "Silverline Home Services",
  "Champion Cooling & Heating",
  "FirstCall Plumbing",
  "EcoAir Solutions",
  "Apex Service Group",
  "BrightStar Mechanical",
  "Valley Comfort Experts",
  "Metro Plumbing Pros",
  "Horizon Home Services",
  "Pacific Air Comfort",
  "Cornerstone Mechanical",
];

// Activity messages with icons
const activities = [
  { message: (name: string) => `${name} just started Strategic Planning`, icon: "🎯" },
  { message: (name: string) => `${name} completed their website strategy`, icon: "✅" },
  { message: (name: string) => `${name} signed their agreement`, icon: "📝" },
  { message: (name: string) => `${name} began onboarding`, icon: "🚀" },
  { message: (name: string) => `${name} launched their new website`, icon: "🌐" },
  { message: (name: string) => `${name} is customizing their growth plan`, icon: "⚡" },
  { message: (name: string) => `${name} added SEO/AEO/AAO marketing`, icon: "📈" },
  { message: (name: string) => `${name} upgraded to Hydra OS`, icon: "💎" },
  { message: (name: string) => `${name} activated AI Local marketing`, icon: "📍" },
  { message: (name: string) => `${name} completed their brand strategy`, icon: "🎨" },
];

// Time descriptions for realism
const timeDescriptions = [
  "just now",
  "a moment ago",
  "2 minutes ago",
  "5 minutes ago",
  "a few minutes ago",
];

const SocialProofToasts = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const usedIndices = useRef<Set<number>>(new Set());

  const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

  const getUniqueCompany = (): string => {
    // Reset if we've used most companies
    if (usedIndices.current.size >= companyNames.length - 2) {
      usedIndices.current.clear();
    }
    let idx: number;
    do {
      idx = Math.floor(Math.random() * companyNames.length);
    } while (usedIndices.current.has(idx));
    usedIndices.current.add(idx);
    return companyNames[idx];
  };

  const showToast = () => {
    const company = getUniqueCompany();
    const activity = getRandomItem(activities);
    const time = getRandomItem(timeDescriptions);

    toast({
      title: `${activity.icon} ${activity.message(company)}`,
      description: time,
      duration: 5000,
    });

    scheduleNext();
  };

  const scheduleNext = () => {
    // Random interval between 2-6 minutes (120,000 - 360,000 ms)
    const delay = Math.floor(Math.random() * (360000 - 120000 + 1)) + 120000;
    timeoutRef.current = setTimeout(showToast, delay);
  };

  useEffect(() => {
    // Start first toast after a random 1-3 minute delay
    const initialDelay = Math.floor(Math.random() * (180000 - 60000 + 1)) + 60000;
    timeoutRef.current = setTimeout(showToast, initialDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return null; // This component doesn't render anything
};

export default SocialProofToasts;
